/**
 * accountingLabelHelpers.ts
 * 정산 관련 라벨/배지 변환 유틸리티 함수
 * useAccountingUtils hook에서 추출된 순수 함수들
 */
import {
    SUPPLIER_STATUS,
    SUPPLIER_STATUS_LABELS,
    RIDER_CONTRACT_STATUS,
    RIDER_CONTRACT_STATUS_LABELS
} from '@/shared/constants/statusCodes';

// ========================================
// 상태 라벨 변환 함수
// ========================================

/**
 * 협력사 상태 배지 텍스트 반환
 * @param statusCode 상태 코드
 */
export const getPartnerStatusBadge = (statusCode: string): string => {
    return SUPPLIER_STATUS_LABELS[statusCode as keyof typeof SUPPLIER_STATUS_LABELS] || statusCode;
};

/**
 * 라이더 계약상태 배지 텍스트 반환
 * @param contStatus 계약 상태 코드
 */
export const getRiderContractStatusBadge = (contStatus: string): string => {
    return RIDER_CONTRACT_STATUS_LABELS[contStatus as keyof typeof RIDER_CONTRACT_STATUS_LABELS] || contStatus;
};

/**
 * 통합 상태 배지 텍스트 반환 (협력사 + 라이더)
 * @param statusCode 상태 코드
 */
export const getStatusBadge = (statusCode: string): string => {
    const statusMap: Record<string, string> = {
        ...SUPPLIER_STATUS_LABELS,
        ...RIDER_CONTRACT_STATUS_LABELS,
        'ACTIVE': '활성',
        'INACTIVE': '비활성',
        'SUSPENDED': '정지',
    };
    return statusMap[statusCode] || statusCode;
};

// ========================================
// 등급 라벨 변환 함수
// ========================================

/**
 * 등급 배지 텍스트 반환
 * @param grade 등급 코드
 */
export const getGradeBadge = (grade: string | null): string => {
    if (!grade) return '-';
    const gradeMap: Record<string, string> = {
        'master': '마스터',
        'gold': '골드',
        'silver': '실버',
        'bronze': '브론즈',
    };
    return gradeMap[grade] || grade;
};

/**
 * 등급별 배지 CSS 클래스 반환
 * @param gradeName 등급명
 */
export const getGradeBadgeClass = (gradeName: string | null | undefined): string => {
    if (!gradeName) return 'badge-grade-none';

    const gradeClassMap: Record<string, string> = {
        '등급없음': 'badge-grade-none',
        '브론즈': 'badge-grade-bronze',
        '실버': 'badge-grade-silver',
        '골드': 'badge-grade-gold',
        '다이아': 'badge-grade-diamond',
        '마스터': 'badge-grade-master',
    };

    return gradeClassMap[gradeName.trim()] || 'badge-grade-none';
};

// ========================================
// CSS 클래스 변환 함수
// ========================================

/**
 * 상태 배지 CSS 클래스 반환
 * @param statusCode 상태 코드
 */
export const getStatusBadgeClass = (statusCode: string): string => {
    const classMap: Record<string, string> = {
        [SUPPLIER_STATUS.OPERATING]: 'badge-success',
        [SUPPLIER_STATUS.TERMINATED]: 'badge-danger',
        [SUPPLIER_STATUS.TERMINATION_REQUESTED]: 'badge-warning',
    };
    return classMap[statusCode] || 'badge-neutral';
};

/**
 * 라이더 계약상태 배지 CSS 클래스 반환
 * @param contStatus 계약 상태 코드
 */
export const getRiderContractStatusBadgeClass = (contStatus: string): string => {
    const classMap: Record<string, string> = {
        [RIDER_CONTRACT_STATUS.UNDER_CONTRACT]: 'badge-success',
        [RIDER_CONTRACT_STATUS.TERMINATED_CONTRACT]: 'badge-danger',
    };
    return classMap[contStatus] || 'badge-neutral';
};

/**
 * 선정산 배지 CSS 클래스 반환
 * @param preAcctYn 선정산 여부
 */
export const getPreAccountBadgeClass = (preAcctYn: string | null): string => {
    return preAcctYn === 'Y' ? 'badge-info' : 'badge-neutral';
};

/**
 * 선정산 배지 텍스트 반환
 * @param preAcctYn 선정산 여부
 */
export const getPreAccountBadgeText = (preAcctYn: string | null): string => {
    return preAcctYn === 'Y' ? '적용' : '미적용';
};

// ========================================
// 테이블 Row 스타일 함수
// ========================================

/**
 * 협력사 테이블 Row CSS 클래스 반환
 * @param statusCode 상태 코드
 * @param balAmt 잔액
 */
export const getPartnerRowClassName = (statusCode: string, balAmt: number | null | undefined): string => {
    if (statusCode === SUPPLIER_STATUS.TERMINATED) {
        return 'terminated-status-row';
    }
    if (balAmt !== null && balAmt !== undefined && balAmt < 0) {
        return 'low-balance-row';
    }
    return '';
};

/**
 * 라이더 테이블 Row CSS 클래스 반환
 * @param contStatus 계약 상태
 * @param balAmt 잔액
 */
export const getRiderRowClassName = (contStatus: string, balAmt: number | null | undefined): string => {
    if (contStatus === RIDER_CONTRACT_STATUS.TERMINATED_CONTRACT) {
        return 'terminated-status-row';
    }
    if (balAmt !== null && balAmt !== undefined && balAmt < 0) {
        return 'low-balance-row';
    }
    return '';
};

// ========================================
// 계좌 상태 확인 함수
// ========================================

/**
 * 협력사 계좌 비활성화 여부
 * @param statusCode 상태 코드
 */
export const isPartnerAccountDisabled = (statusCode: string): boolean => {
    return statusCode === SUPPLIER_STATUS.TERMINATED;
};

/**
 * 라이더 계좌 비활성화 여부
 * @param contStatus 계약 상태
 */
export const isRiderAccountDisabled = (contStatus: string): boolean => {
    return contStatus === RIDER_CONTRACT_STATUS.TERMINATED_CONTRACT;
};
